import { FunctionComponent, Suspense } from 'react'
import dayjs from 'dayjs'
import { trim } from 'lodash'

import Modal from '..'
import PopupModal from '../PopupModal'
import Field from '../../fields'
import { Resident } from '@lmc/ts-common'

interface Medication {
  id: string
  name: string
}

interface EmarDiscontinueMedModalProps {
  fields: any
  isOpen: boolean
  medication: Medication
  resident: Resident
  showToast: (props: any) => void
  updateItem: (props: any) => void
}

const handleComplete = (props: any) => {
  const { fetchList, showToast } = props

  fetchList('emar-resident-medications')
  showToast({ title: 'Discontinued a Medication' })
}

const handleError = (props: any) => {
  const { error, showToast } = props

  console.log(error)
  showToast({ type: 'error', title: 'Something went wrong...' })
}

const handleSubmit = (props: EmarDiscontinueMedModalProps) => {
  const { fields, medication, updateItem } = props

  updateItem({
    url: `list/emar-resident-medications/${medication.id}`,
    json: {
      deleted: true,
      deletedOn: dayjs().toISOString(),
      reasonDeleted: fields.reasonDeleted,
    },
    onComplete: () => handleComplete(props),
    onError: (error: any) => handleError({ ...props, error }),
  })
}

/**
 * Renders modal for discontinuing a medication
 *
 * @param {object} medication - The medication to be discontinued.
 * @param {object} resident - The resident that the medication is assigned to.
 *
 * @returns {FunctionComponent<EmarDiscontinueMedModalProps>} EmarDiscontinueMedModal
 */
const EmarDiscontinueMedModal: FunctionComponent<
  EmarDiscontinueMedModalProps
> = (props) => {
  const { fields, isOpen, medication, resident, ...formProps } = props

  if (!isOpen) return null

  return (
    <PopupModal
      buttonDisabled={!trim(fields.reasonDeleted)}
      buttonIcon="trashcan"
      buttonStyle={styles.discontinueBtn}
      buttonType="discontinue"
      confirmText="Discontinue"
      footerStyle={styles.footerStyle}
      onConfirm={() => handleSubmit(props)}
      style={styles.popupModal}
      supportArticle="https://support.logmycare.co.uk/en/articles/4851811-how-do-i-discontinue-a-medication"
      title="Discontinue Medication"
      {...formProps}
    >
      <div style={styles.note}>
        Discontinuing this medication will mean that carers will no longer be
        able to sign for it in the App. The medication will move to the History
        section.
      </div>
      <div style={styles.subtitle}>
        {`Discontinue ${medication.name} for ${resident.name.first} ${resident.name.last}`}
      </div>
      <div style={styles.reasonFieldTitle}>
        Why is this medication being discontinued?
      </div>
      <Suspense fallback={<div>Loading...</div>}>
      <Field
        formId={MODAL_ID}
        keys="reasonDeleted"
        layout="no-label"
        style={styles.reasonField}
        type="textarea"
        values={fields}
      />
      </Suspense>
    </PopupModal>
  )
}

const MODAL_ID = 'discontinueMed'

const styles = {
  discontinueBtn: {
    padding: '0 1em',
  },
  footerStyle: {
    justifyContent: 'space-between',
  },
  note: {
    fontSize: 12,
    lineHeight: '18px',
    marginBottom: 16,
  },
  popupModal: {
    maxWidth: 630,
  },
  reasonField: {
    minHeight: 90,
  },
  reasonFieldTitle: {
    fontWeight: 700,
    lineHeight: '22px',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    fontWeight: 400,
    marginBottom: 16,
  },
}

export default Modal(EmarDiscontinueMedModal, MODAL_ID)
